import type { DriveExportResult, GithubOrg, GithubPublishInput, GithubRepo, GithubStatus, GoogleSignInSettings, GoogleStatus, ImportPlan, ProjectGithubStatus, PublicUser } from "@codvero/shared";
import { ApiFailure, api, del, get, post } from "./client.ts";

async function multipart<T>(path: string, fd: FormData): Promise<T> {
  const res = await fetch(path, { method: "POST", body: fd, credentials: "same-origin" });
  const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!res.ok) throw new ApiFailure((body.error as never) ?? "internal", res.status, body.message);
  return body as T;
}

export const importsApi = {
  /** Folder upload: File objects plus their relative paths (webkitRelativePath). */
  stageFiles: (projectId: string, files: { file: File; path: string }[]) => {
    const fd = new FormData();
    for (const f of files) fd.append("file", f.file, f.file.name);
    fd.set("paths", JSON.stringify(files.map((f) => f.path)));
    return multipart<ImportPlan>(`/api/projects/${projectId}/import/stage`, fd);
  },
  stageZip: (projectId: string, zip: File) => {
    const fd = new FormData();
    fd.set("zip", zip, zip.name);
    return multipart<ImportPlan>(`/api/projects/${projectId}/import/stage`, fd);
  },
  plan: (projectId: string, importId: string) => get<ImportPlan>(`/api/projects/${projectId}/import/${importId}`),
  apply: (projectId: string, importId: string, input: { mode: "merge" | "replace"; include: string[] }) => post<{ files: number }>(`/api/projects/${projectId}/import/${importId}/apply`, input),
  discard: (projectId: string, importId: string) => del(`/api/projects/${projectId}/import/${importId}`),
  clone: (projectId: string, input: { url: string; branch?: string; mode: "merge" | "replace" }) => api<{ output: string }>(`/api/projects/${projectId}/clone`, { method: "POST", json: input }),
};

export const githubApi = {
  status: () => get<GithubStatus>("/api/integrations/github/status"),
  startUrl: "/api/integrations/github/start",
  disconnect: () => post<void>("/api/integrations/github/disconnect"),
  repos: (q = "") => get<GithubRepo[]>(`/api/integrations/github/repos?q=${encodeURIComponent(q)}`),
  orgs: () => get<GithubOrg[]>("/api/integrations/github/orgs"),
  setApp: (clientId: string, clientSecret: string) => api<GithubStatus>("/api/integrations/github/app", { method: "PUT", json: { clientId, clientSecret } }),
  clearApp: () => del<GithubStatus>("/api/integrations/github/app"),
};

export const publishApi = {
  status: (projectId: string) => get<ProjectGithubStatus>(`/api/projects/${projectId}/github`),
  publish: (projectId: string, input: GithubPublishInput) => post<ProjectGithubStatus>(`/api/projects/${projectId}/github/publish`, input),
  push: (projectId: string, message: string) => post<ProjectGithubStatus>(`/api/projects/${projectId}/github/push`, { message }),
  pull: (projectId: string) => post<ProjectGithubStatus>(`/api/projects/${projectId}/github/pull`),
  mergePr: (projectId: string) => post<ProjectGithubStatus>(`/api/projects/${projectId}/github/pr/merge`),
  unlink: (projectId: string) => del(`/api/projects/${projectId}/github`),
};

export const googleApi = {
  status: () => get<GoogleStatus>("/api/integrations/google/status"),
  startUrl: "/api/integrations/google/start",
  signInUrl: "/api/auth/google/start",
  disconnect: () => post<void>("/api/integrations/google/disconnect"),
  exportDrive: (projectId: string) => post<DriveExportResult>(`/api/projects/${projectId}/export/drive`),
  signInSettings: () => get<GoogleSignInSettings & { configured: boolean }>("/api/settings/google-signin"),
  setSignInSettings: (v: GoogleSignInSettings) => api<GoogleSignInSettings & { configured: boolean }>("/api/settings/google-signin", { method: "PUT", json: v }),
  totp: (token: string, code: string) => post<{ status: "ok"; user: PublicUser }>("/api/auth/google/totp", { token, code }),
};
